import { ReactNode, useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { COMPLIANCE_ERROR_TYPES } from 'src/constants/compliance';
import { ROUTES } from 'src/constants/routes';
import { useLocationSearch } from 'src/helpers/hooks/useLocationSearch';
import { useMount } from 'src/helpers/hooks/useMount';
import { Loader } from 'src/pages/StartApplication/StartApplication.loader';
import { getUtmSource } from 'src/redux/selectors/system';
import {
  getVerifyPolicyComplianceProvider,
  getVerifyPolicyFlow,
  getVerifyPolicyInput,
} from 'src/redux/selectors/verifyPolicy';
import { validateApplicationPolicy } from '../api/validate-application-policy.api';
import { RevalidateApplicationDto } from '../types';
import { displayErrorToast } from '../utils/display-error-toast.util';
import { getErrorMessage } from '../utils/get-error-message.util';
import { handleApplicationStatus } from '../utils/handle-application-status.util';
import { validateVerifyPolicyInputData } from '../utils/validate-verify-policy-input-data.util';

type ComplianceApplicationRevalidatorStatus = 'loading' | 'parsed';

interface ComplianceApplicationRevalidatorProps {
  children: ReactNode;
}

export function ComplianceApplicationRevalidator({
  children,
}: ComplianceApplicationRevalidatorProps) {
  const [loadingStatus, setLoadingStatus] =
    useState<ComplianceApplicationRevalidatorStatus>('loading');

  const navigate = useNavigate();
  const getUrlWithQueryParams = useLocationSearch();

  const verifyPolicyInput = useSelector(getVerifyPolicyInput);
  const verifyPolicyFlow = useSelector(getVerifyPolicyFlow);
  const verifyPolicyComplianceProviderData = useSelector(
    getVerifyPolicyComplianceProvider,
  );
  const utmSource = useSelector(getUtmSource);

  const isValidQuoteData = useMemo(
    () => validateVerifyPolicyInputData(verifyPolicyInput),
    [verifyPolicyInput],
  );

  const revalidateApplicationPolicy = useCallback(async () => {
    if (
      !verifyPolicyComplianceProviderData?.activeCovieLink ||
      !isValidQuoteData ||
      !verifyPolicyInput
    ) {
      return;
    }

    const requestDto: RevalidateApplicationDto = {
      address: verifyPolicyInput.address,
      email: verifyPolicyInput.email,
      firstName: verifyPolicyInput.firstName,
      lastName: verifyPolicyInput.lastName,
      leaseStart: verifyPolicyInput.leaseStart ?? '',
      leaseEnd: verifyPolicyInput.leaseEnd ?? '',
      buildingId: verifyPolicyInput.buildingId,
      linkId: verifyPolicyComplianceProviderData.activeCovieLink,
      buildingUnit: verifyPolicyInput.buildingUnit,
      utmSource,
    };

    const applicationPolicyResponseData = await validateApplicationPolicy(
      requestDto,
    );
    const applicationId = applicationPolicyResponseData?.application?.id;
    const validationErrors = applicationPolicyResponseData?.validation?.errors;

    if (!applicationId) {
      if (validationErrors) {
        const responseErrors = applicationPolicyResponseData?.errors;
        const errorType = responseErrors[0].type;

        if (
          errorType === COMPLIANCE_ERROR_TYPES.APPLICATION_ALREADY_COMPLIANT
        ) {
          navigate(
            getUrlWithQueryParams(ROUTES.COMPLIANCE_ALREADY_VERIFIED_PATH),
          );
          return;
        }

        return;
      }

      displayErrorToast('Unknown response');
      return;
    }

    const routeToNavigateTo = handleApplicationStatus(
      applicationPolicyResponseData,
    );
    if (routeToNavigateTo) {
      navigate(routeToNavigateTo, { replace: true });
      return;
    }
  }, [
    getUrlWithQueryParams,
    isValidQuoteData,
    navigate,
    utmSource,
    verifyPolicyComplianceProviderData.activeCovieLink,
    verifyPolicyInput,
  ]);

  useMount(() => {
    const shouldRevalidateApplicationPolicy =
      verifyPolicyFlow.hasClickedComplianceContinue !== true &&
      !!verifyPolicyComplianceProviderData?.activeCovieLink;

    if (!shouldRevalidateApplicationPolicy) {
      setLoadingStatus('parsed');
      return;
    }

    revalidateApplicationPolicy()
      .catch((error) => {
        const errorMessage = getErrorMessage(error);
        displayErrorToast(errorMessage);
      })
      .finally(() => {
        setLoadingStatus('parsed');
      });
  });

  if (loadingStatus === 'loading') {
    return <Loader />;
  }

  return <>{children}</>;
}
